/**
 * AssetLoader — promise-cached GLB loader for the viewer.
 *
 * Performance strategy: exactly one GLB is loaded per module / mover type,
 * then `clone()` is called per instance. The loader is keyed by absolute URL
 * (concat of assetsBaseUrl + entry filename), and concurrent loads of the
 * same URL share a single in-flight Promise.
 *
 * Asset failure: a 404 (or any fetch error) surfaces as
 * XtsViewerErrorException(code='asset-load-failed'). The renderer is
 * expected to fall back to a wireframe-AABB placeholder.
 *
 * Concurrent-cancel: when `cancel()` is called for a URL, future awaiters
 * receive an AbortError. The cache entry is cleared so a fresh `load()`
 * triggers a new fetch.
 */

import { GLTFLoader } from 'three-stdlib';
import type { GLTF } from 'three-stdlib';
import type { WebGLRenderer } from 'three';
import { XtsViewerErrorException } from '../types.js';
import {
  configureLoader,
  type ConfigureLoaderOptions,
} from './configureGltfLoaders.js';

interface CacheEntry {
  url: string;
  promise: Promise<GLTF>;
  controller?: AbortController;
}

export class AssetLoader {
  private readonly cache = new Map<string, CacheEntry>();
  private readonly loader = new GLTFLoader();

  /**
   * Wire compression decoders (Meshopt always; KTX2 when a renderer is
   * supplied) onto the underlying GLTFLoader so Meshopt/KTX2-compressed GLBs
   * parse. Safe to call repeatedly and before/after loads — it only attaches
   * decoders and does not touch the cache. Without `gl`, only Meshopt
   * geometry is wired (KTX2 needs the renderer for GPU-format detection).
   */
  configure(options: ConfigureLoaderOptions & { gl?: WebGLRenderer | null } = {}): void {
    configureLoader(this.loader, options);
  }

  /**
   * Load a GLB. Repeated calls for the same URL return the cached Promise.
   * Returns the parsed GLTF object — the consumer is responsible for
   * cloning the scene per instance.
   */
  load(url: string): Promise<GLTF> {
    const existing = this.cache.get(url);
    if (existing) return existing.promise;

    const controller = new AbortController();
    const promise = this.fetchAndParse(url, controller.signal).catch((err) => {
      // Drop the cache entry on failure so retries can happen.
      this.cache.delete(url);
      throw err;
    });
    this.cache.set(url, { url, promise, controller });
    return promise;
  }

  /**
   * Pre-load multiple URLs in parallel. Resolves once all complete, even
   * if some failed (failed entries appear as `null`).
   */
  async preload(urls: ReadonlyArray<string>): Promise<Array<GLTF | null>> {
    return Promise.all(
      urls.map((u) =>
        this.load(u).catch(() => null),
      ),
    );
  }

  /** Cancel an in-flight load and remove the cache entry. */
  cancel(url: string): void {
    const entry = this.cache.get(url);
    if (!entry) return;
    entry.controller?.abort();
    this.cache.delete(url);
  }

  /** Clear all cache entries. In-flight loads are NOT aborted. */
  clear(): void {
    this.cache.clear();
  }

  /** Returns true if a load is in progress / completed for this URL. */
  has(url: string): boolean {
    return this.cache.has(url);
  }

  private async fetchAndParse(
    url: string,
    signal: AbortSignal,
  ): Promise<GLTF> {
    let arrayBuffer: ArrayBuffer;
    try {
      const res = await fetch(url, { signal });
      if (!res.ok) {
        throw new XtsViewerErrorException(
          'asset-load-failed',
          `Failed to fetch GLB at "${url}" (status ${res.status})`,
          { url, status: res.status },
        );
      }
      arrayBuffer = await res.arrayBuffer();
    } catch (err) {
      if (err instanceof XtsViewerErrorException) throw err;
      if ((err as Error)?.name === 'AbortError') {
        throw err;
      }
      throw new XtsViewerErrorException(
        'asset-load-failed',
        `Network error while fetching GLB "${url}": ${(err as Error)?.message}`,
        { url, cause: err },
      );
    }

    return new Promise<GLTF>((resolve, reject) => {
      try {
        this.loader.parse(
          arrayBuffer,
          // resourcePath: GLB embeds bin/textures, no external resources expected
          '',
          (gltf) => resolve(gltf),
          (err: unknown) =>
            reject(
              new XtsViewerErrorException(
                'asset-load-failed',
                `Failed to parse GLB "${url}": ${(err as Error)?.message ?? err}`,
                { url, cause: err },
              ),
            ),
        );
      } catch (err) {
        reject(
          new XtsViewerErrorException(
            'asset-load-failed',
            `Threw while parsing GLB "${url}": ${(err as Error)?.message}`,
            { url, cause: err },
          ),
        );
      }
    });
  }
}

/** Module-level singleton for the most common case. */
let SHARED: AssetLoader | undefined;
export function getSharedAssetLoader(): AssetLoader {
  if (!SHARED) SHARED = new AssetLoader();
  return SHARED;
}

/** Compose a URL from a base + a filename, normalising trailing slashes. */
export function composeAssetUrl(baseUrl: string, filename: string): string {
  if (!filename) return '';
  if (/^https?:\/\//.test(filename) || filename.startsWith('/')) return filename;
  const trimmed = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${trimmed}/${filename}`;
}
